import React from 'react'
import { Box, Stack, Typography, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import type { ReleaseNote } from './releaseNotes'
import ReleaseNoteCard from './ReleaseNoteCard'

interface ChangelogPanelProps {
  anchor?: 'sidebar' | 'topbar'
  hasMoreReleaseNotes: boolean
  hasNoReleaseNotes: boolean
  language: string
  onClose: () => void
  onLoadMore: () => void
  releaseNotes: ReleaseNote[]
}

const ChangelogPanel = React.forwardRef<HTMLDivElement, ChangelogPanelProps>(({
  anchor = 'sidebar',
  hasMoreReleaseNotes,
  hasNoReleaseNotes,
  language,
  onClose,
  onLoadMore,
  releaseNotes,
}, ref) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const isTopbar = anchor === 'topbar'

  return (
    <Box
      ref={ref}
      sx={{
        position: isTopbar ? 'absolute' : 'fixed',
        ...(isTopbar
          ? {
            top: 'calc(100% + 14px)',
            right: 0
          }
          : {
            left: 18,
            bottom: 66
          }),
        zIndex: 1300,
        width: isTopbar ? 420 : 420,
        maxWidth: 'calc(100vw - 36px)',
        maxHeight: 'min(560px, calc(100vh - 96px))',
        overflow: isTopbar ? 'visible' : 'hidden',
        border: '1px solid #F7F7F7',
        borderRadius: '16px',
        backgroundColor: 'white',
        boxShadow: '0px 1.85px 3.15px 0px #00000014, 0px 8.15px 6.52px 0px #0000000A, 0px 20px 13px 0px #0000000A',
        ...(isTopbar && {
          '&::before': {
            content: '""',
            position: 'absolute',
            top: -7,
            right: 19,
            width: 12,
            height: 12,
            backgroundColor: 'white',
            borderLeft: '1px solid #F7F7F7',
            borderTop: '1px solid #F7F7F7',
            transform: 'rotate(45deg)'
          }
        })
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
        sx={{
          px: 2,
          py: 1.25
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center" minWidth={0}>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              backgroundColor: '#EFF6FF',
              color: theme.palette.primary.main,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            <i className="fa-light fa-list-timeline" style={{ fontSize: 13 }} />
          </Box>
          <Box minWidth={0}>
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#282828' }}>
              {t('changelog.title')}
            </Typography>
            <Typography sx={{ fontSize: 11, fontWeight: 400, color: theme.palette.grey[600] }}>
              {t('changelog.subtitle')}
            </Typography>
          </Box>
        </Stack>
        <Box
          component="button"
          type="button"
          aria-label={t('changelog.close')}
          onClick={onClose}
          sx={{
            width: 28,
            height: 28,
            p: 0,
            border: 0,
            borderRadius: '50%',
            backgroundColor: 'transparent',
            color: theme.palette.grey[600],
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            '&:hover': {
              backgroundColor: theme.palette.grey[50],
              color: theme.palette.primary.main
            }
          }}
        >
          <i className="fa-light fa-xmark" style={{ fontSize: 14 }} />
        </Box>
      </Stack>
      <Stack
        spacing={1}
        sx={{
          px: 1.5,
          pb: 1.5,
          pt: 0.75,
          maxHeight: 'calc(min(560px, calc(100vh - 96px)) - 74px)',
          overflowY: 'auto'
        }}
      >
        {hasNoReleaseNotes && (
          <Typography sx={{ fontSize: 12, color: theme.palette.grey[600] }}>
            {t('changelog.empty')}
          </Typography>
        )}
        {releaseNotes.map(note => (
          <ReleaseNoteCard
            key={note.version}
            language={language}
            note={note}
          />
        ))}
        {hasMoreReleaseNotes && (
          <Box
            component="button"
            type="button"
            onClick={onLoadMore}
            sx={{
              width: '100%',
              px: 1.25,
              py: 0.9,
              border: `1px solid ${theme.palette.grey[200]}`,
              borderRadius: '6px',
              backgroundColor: 'white',
              color: theme.palette.primary.main,
              cursor: 'pointer',
              fontSize: 12,
              fontWeight: 600,
              fontFamily: 'inherit',
              transition: 'border-color 0.15s ease, background-color 0.15s ease',
              '&:hover': {
                borderColor: theme.palette.primary.main,
                backgroundColor: '#EFF6FF'
              }
            }}
          >
            {t('changelog.load_more')}
          </Box>
        )}
      </Stack>
    </Box>
  )
})

ChangelogPanel.displayName = 'ChangelogPanel'

export default ChangelogPanel
